import { useCallback, useEffect, useState } from 'react';
import { Rocket, RefreshCw, LogOut, FileText, Users, HelpCircle, Search, Inbox, Settings2, AlertTriangle, BarChart3 } from 'lucide-react';
import nativeCms from '../data/cms.json';
import {
  fetchDraftCms,
  saveDraft,
  publishCms,
  publishStatus,
  resetDraft,
  refreshAiFiles,
  login as apiLogin,
} from '../lib/cms-api';
import type { CmsData } from '../types/cms';
import ContentEditor from '../components/admin/ContentEditor';
import ConsultantsManager from '../components/admin/ConsultantsManager';
import FaqManager from '../components/admin/FaqManager';
import SeoManager from '../components/admin/SeoManager';
import InquiriesManager from '../components/admin/InquiriesManager';
import AnalyticsView from '../components/admin/AnalyticsView';

const NATIVE = nativeCms as unknown as CmsData;

type TabKey = 'publish' | 'content' | 'consultants' | 'faq' | 'seo' | 'inquiries' | 'analytics';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'publish', label: '發布管理', icon: <Rocket className="h-4 w-4" /> },
  { key: 'content', label: '網站內容', icon: <FileText className="h-4 w-4" /> },
  { key: 'consultants', label: '顧問團隊', icon: <Users className="h-4 w-4" /> },
  { key: 'faq', label: 'FAQ 管理', icon: <HelpCircle className="h-4 w-4" /> },
  { key: 'seo', label: 'SEO 設定', icon: <Search className="h-4 w-4" /> },
  { key: 'inquiries', label: '查詢管理', icon: <Inbox className="h-4 w-4" /> },
  { key: 'analytics', label: '進站分析', icon: <BarChart3 className="h-4 w-4" /> },
];

export interface AdminCommon {
  draft: CmsData;
  onSave: (partial: Record<string, unknown>) => Promise<void>;
  notify: (msg: string, type?: 'success' | 'error') => void;
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tokenInput, setTokenInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [draft, setDraft] = useState<CmsData>(NATIVE);
  const [status, setStatus] = useState<any>(null);
  const [tab, setTab] = useState<TabKey>('publish');

  // Publish progress (E-Corp style: explicit publish with visible progress)
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const notify = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadDraft = useCallback(async () => {
    try {
      const data = await fetchDraftCms();
      setDraft(data);
      setAuthed(true);
    } catch (e: any) {
      if (String(e?.message || e).includes('401')) {
        localStorage.removeItem('gl_admin_token');
        setAuthed(false);
      } else {
        notify('無法連線 CMS API，請確認 Worker 已部署。', 'error');
      }
    } finally {
      setChecking(false);
    }
  }, [notify]);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await publishStatus());
    } catch {
      /* status is non-critical */
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('gl_admin_token');
    if (token) {
      loadDraft().then(refreshStatus);
    } else {
      setChecking(false);
    }
  }, [loadDraft, refreshStatus]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      await apiLogin(tokenInput);
      localStorage.setItem('gl_admin_token', tokenInput);
      await loadDraft();
      refreshStatus();
    } catch {
      setLoginError('存取碼錯誤，請重新輸入。');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('gl_admin_token');
    setAuthed(false);
    setTokenInput('');
  };

  const onSave = useCallback(
    async (partial: Record<string, unknown>) => {
      try {
        await saveDraft(partial);
        const fresh = await fetchDraftCms();
        setDraft(fresh);
        notify('已儲存為草稿。點擊「一鍵發布」才會更新網站。');
        refreshStatus();
      } catch (e) {
        console.error(e);
        notify('儲存失敗，請重試。', 'error');
      }
    },
    [notify, refreshStatus]
  );

  const handlePublish = async () => {
    setPublishing(true);
    try {
      setProgress(10);
      setProgressLabel('正在準備發布…');
      await new Promise((r) => setTimeout(r, 300));

      setProgress(35);
      setProgressLabel('正在驗證草稿資料…');
      const fresh = await fetchDraftCms();
      if (!fresh || !fresh.version) throw new Error('草稿資料不完整');

      setProgress(60);
      setProgressLabel('正在發布至生產環境…');
      await publishCms();

      setProgress(85);
      setProgressLabel('正在重新生成 AI 檔案（llms / sitemap）…');
      await refreshAiFiles().catch(() => null);

      setProgress(100);
      setProgressLabel('發布完成！');
      notify('已發布。網站將於 1-2 分鐘內更新（KV 快取傳播）。');
      refreshStatus();
    } catch (e) {
      console.error(e);
      notify('發布失敗，請重試。', 'error');
    } finally {
      setTimeout(() => {
        setPublishing(false);
        setProgress(0);
        setProgressLabel('');
      }, 900);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('確定要將草稿重設為預設內容嗎？所有未發布的修改將會消失（已發布的網站不受影響）。')) return;
    try {
      await resetDraft();
      await loadDraft();
      notify('草稿已重設為預設內容。');
      refreshStatus();
    } catch {
      notify('重設失敗，請重試。', 'error');
    }
  };

  const handleRefreshAi = async () => {
    try {
      const res = await refreshAiFiles();
      notify(`AI 內容已重新生成：${Object.entries(res.sizes || {}).map(([k, v]) => `${k} ${v}B`).join('、')}`);
    } catch {
      notify('重新生成失敗，請稍後再試。', 'error');
    }
  };

  // ------------------------------------------------------------------
  // Login screen
  // ------------------------------------------------------------------
  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">載入中…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h1 className="text-2xl font-bold mb-2 text-center text-navy-900">CMS 管理後台</h1>
          <p className="text-sm text-gray-500 mb-6 text-center">匯通環球策略 · Global Link Strategy</p>
          <label className="block text-sm font-medium text-gray-700 mb-1">管理員存取碼</label>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:ring-2 focus:ring-navy-200 outline-none"
            placeholder="請輸入 ADMIN_TOKEN"
            autoFocus
          />
          {loginError && <p className="text-red-600 text-sm mb-3">{loginError}</p>}
          <button
            type="submit"
            className="w-full bg-navy-900 text-white py-2.5 rounded hover:bg-navy-800 transition-colors"
          >
            登入
          </button>
        </form>
      </div>
    );
  }

  const dirty = status?.isDirty;

  // ------------------------------------------------------------------
  // Dashboard
  // ------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-navy-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">CMS 管理後台</h1>
            <p className="text-xs text-gray-400">匯通環球策略 · Global Link Strategy</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-sm px-3 py-2 rounded bg-navy-800 hover:bg-navy-700 transition-colors"
            >
              查看網站
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-sm px-3 py-2 rounded bg-red-500/90 hover:bg-red-600 transition-colors"
            >
              <LogOut className="h-4 w-4" /> 登出
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                tab === key ? 'border-gold-500 text-gold-400' : 'border-transparent text-gray-300 hover:text-white'
              }`}
            >
              {icon}
              {label}
              {key === 'publish' && dirty && (
                <span className="ml-1 h-2 w-2 rounded-full bg-gold-400 inline-block" title="有未發布變更" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Publish status card — always visible */}
        <div className="bg-white rounded-lg shadow-sm border p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  dirty ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                }`}
              >
                {dirty ? '有未發布變更' : '已同步發布'}
              </span>
              <span className="text-sm text-gray-500">
                {status?.publishedAt
                  ? `上次發布：${new Date(status.publishedAt).toLocaleString('zh-HK')}`
                  : '尚未發布過（網站使用預設內容）'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshAi}
                className="flex items-center gap-1.5 text-sm px-4 py-2 rounded border border-navy-200 text-navy-700 hover:bg-navy-50 transition-colors"
                title="由發布資料重新生成 llms.txt / llms-full.txt / sitemap.xml"
              >
                <RefreshCw className="h-4 w-4" /> 刷新 AI 內容
              </button>
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-sm px-4 py-2 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              >
                <AlertTriangle className="h-4 w-4" /> 重設草稿
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing || !dirty}
                className="flex items-center gap-1.5 text-sm px-5 py-2 rounded bg-gold-500 hover:bg-gold-600 text-navy-900 font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={dirty ? '發布草稿到網站' : '沒有未發布的變更'}
              >
                <Rocket className="h-4 w-4" /> 一鍵發布
              </button>
            </div>
          </div>
          {publishing && (
            <div className="mt-4">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">{progressLabel}</p>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            提示：所有分頁的「儲存」只會寫入草稿；按「一鍵發布」後，網站內容與 AI 檔案於 1-2 分鐘內更新；網頁原始碼（搜尋引擎／AI 讀取的預渲染 HTML）會隨後自動重建。
          </p>
        </div>

        {/* Tab panels */}
        {tab === 'publish' && <PublishInfo status={status} />}
        {tab === 'content' && <ContentEditor draft={draft} onSave={onSave} notify={notify} />}
        {tab === 'consultants' && <ConsultantsManager draft={draft} onSave={onSave} notify={notify} />}
        {tab === 'faq' && <FaqManager draft={draft} onSave={onSave} notify={notify} />}
        {tab === 'seo' && <SeoManager draft={draft} onSave={onSave} notify={notify} />}
        {tab === 'inquiries' && <InquiriesManager notify={notify} />}
        {tab === 'analytics' && <AnalyticsView />}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-sm max-w-sm ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function PublishInfo({ status }: { status: any }) {
  const cards = [
    { label: '草稿狀態', value: status?.hasDraft ? '存在' : '不存在（使用預設值）' },
    { label: '發布狀態', value: status?.hasPublished ? `已於 ${new Date(status.publishedAt).toLocaleString('zh-HK')} 發布` : '尚未發布' },
    { label: '未發布變更', value: status?.isDirty ? '有 — 請點擊「一鍵發布」' : '沒有' },
    { label: 'AI 檔案端點', value: '/api/public/llms.txt · /api/public/llms-full.txt · /api/public/sitemap.xml' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-lg shadow-sm border p-5">
          <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
            <Settings2 className="h-3.5 w-3.5" /> {c.label}
          </p>
          <p className="text-sm text-gray-800 break-all">{c.value}</p>
        </div>
      ))}
      <div className="md:col-span-2 bg-white rounded-lg shadow-sm border p-5">
        <p className="text-xs text-gray-400 mb-2">發布流程</p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
          <li>在「網站內容／顧問團隊／FAQ／SEO」分頁修改並儲存（寫入草稿）。</li>
          <li>回到「發布管理」，確認狀態為「有未發布變更」。</li>
          <li>點擊「一鍵發布」— 系統會將草稿同步到網站並重新生成 AI 檔案。</li>
          <li>約 1-2 分鐘後（KV 傳播），重新整理網站即可看到更新。</li>
        </ol>
      </div>
    </div>
  );
}
